import { Disposable } from '../common/disposable';
import { injectService } from '../ioc/inject';
import type { interfaces } from '../shared/inversify';
import { inject, injectable, optional } from '../shared/inversify';
import type { Deferred } from '../utils/deferred';
import { create } from '../utils/deferred';
import { DialogManager } from './dialog-manager';
import type { CancelCallback, SubmitCallback, DialogService, DialogOptions } from './request-input-service';

/**
 * Represents the callbacks for a dialog.
 * @template T - The type of data to be submitted.
 * @template K - The type of the response.
 */
export interface DialogCallbacks<T, K = unknown> {
    /**
     * Submits the data.
     * @param data - The data to be submitted from the dialog by clicking the submit button.
     * @returns A promise that resolves when the submit data is processed.
     */
    submit: (data: T) => Promise<K>;

    /**
     * Aborts the dialog.
     */
    abort: () => void;
}

/**
 * Represents a route guard which can be used to prevent navigation when a dialog is open.
 */
export const RouteGuard = Symbol.for('RouteGuard');

/**
 * Represents a route guard which can be used to prevent navigation when a dialog is open.
 */
export type RouteGuard = {
    beforeEach: (fn: (to: unknown, from: unknown, next: (arg?: boolean) => void) => void) => (() => void);
}

type AsyncComponent = () => Promise<any>;

/**
 * Represents a service for handling dialog inputs.
 */
@injectable()
export abstract class AbstractDialogInputService<T extends DialogOptions, K> implements DialogService<T, K> {
    /**
     * The VUE component to be displayed in the dialog.
     */
    protected abstract component: AsyncComponent;

    @inject(DialogManager)
    protected readonly _dialogManager!: DialogManager;

    @optional()
    @inject(RouteGuard)
    protected routeGuard?: RouteGuard;

    private _submitCallback: SubmitCallback<K> = (data, next) => { next(true) };
    private _cancelCallback: CancelCallback = () => { };
    private _deferred!: Deferred<K>;

    /**
     * Registers a callback to be called when the cancel button is clicked.
     * @param callback The callback function to be called when the cancel button is clicked.
     */
    onCancel(callback: CancelCallback): void {
        this._cancelCallback = callback;
    }

    /**
     * 打开对话框
     * @param DialogOptions 
     * @returns 
     */
    open(dialogOptions?: T): Promise<K> {
        this._deferred = create<K>();
        const props = {
            ...dialogOptions,
            callbacks: {
                submit: (data: K) => {
                    return this._invokeSubmitCallback(data);
                },
                abort: () => {
                    return this._invokeCancelCallback();
                },
            },
            onSubmit: (data: K) => {
                return this._invokeSubmitCallback(data);
            },
            onCancel: () => {
                return this._invokeCancelCallback();
            },
        };
        this._dialogManager.update(this.component, props);
        this._dialogManager.toggleOpenState(this.component, true);
        this._setRouteGuard();
        return this._deferred.promise;
    }

    /**
     * 关闭对话框
     */
    close() {
        this._invokeCancelCallback();
    }
    
    /**
     * Registers a callback to be called  when the submit button is clicked.
     * @param callback The callback function to be called when the submit button is clicked.
     */
    onSubmit(callback: SubmitCallback<K>): void {
        this._submitCallback = callback;
    }

    /**
     * @deprecated Use `onSubmit` instead.
     */
    onReceived(callback: SubmitCallback<K>): void {
        this.onSubmit(callback);
    }

    /**
     * @deprecated Use `onCancel` instead.
     */
    onAborted(callback: CancelCallback): void {
        this.onCancel(callback);
    }

    /**
     * @deprecated Use `open` instead.
     */
    request(dialogOptions?: T): Promise<K> {
        return this.open(dialogOptions);
    }

    /**
     * @deprecated Use `close` instead.
     */
    abort() {
        this.close();
    }

    /**
     * 执行通过 onSubmit 注册的回调函数，并关闭对话框
     * @param data 
     * @returns 
     */
    private async _invokeSubmitCallback(data: K) {
        await this._submitCallback(data, (bool = true) => {
            if (bool) {
                this._closeDialog();
                this._deferred.resolve(data);
            }
        });
        return this._deferred.promise;
    }

    /**
     * 执行通过 onCancel 注册的回调函数，并关闭对话框
     */
    private _invokeCancelCallback() {
        this._cancelCallback();
        this._closeDialog();
        this._dispose();
        this._deferred.promise.catch(() => {});
        this._deferred.reject();
        return;
    }

    private _disposes: Disposable[] = [];

    private _dispose() {
        this._disposes.forEach(disposable => {
            disposable.dispose();
        });
        this._disposes.length = 0;
    }

    private _routeGuardDispose: () => void = () => {};

    private _setRouteGuard() {
        this._routeGuardDispose();
        this._routeGuardDispose = this.routeGuard?.beforeEach((to, from, next) => {
            const has = this._dialogManager.hasOpenDialog();
            if (has) {
                next(false);
            } else {
                next();
            }
        }) || (() => {});

        this._disposes.push({ dispose: () => {
            this._routeGuardDispose();
        }});
    }

    private _closeDialog() {
        this._dialogManager.toggleOpenState(this.component, false);
        this._dialogManager.unregister(this.component);
    }
}

function defineProperty(target: any, propertyKey: string, instance: any) {
    Object.defineProperty(target, propertyKey, {
        get() {
            return instance;
        },
    });
}

/**
 * Manually inject a service into a property
 */
function manualInject(target: any, propertyKey: string, bindId: interfaces.ServiceIdentifier<any>) {
    Object.defineProperty(target, propertyKey, {
        get() {
            return injectService(bindId);
        },
    });
}

export function createDialogInstance(component: AsyncComponent) {
    function create(): new() => DialogService<any, any> {
        return class A extends AbstractDialogInputService<any, any> {
            protected component = component;
        }
    }

    const instance = new (create())();

    manualInject(instance, '_dialogManager', DialogManager);
    manualInject(instance, 'routeGuard', RouteGuard);
    return instance;
}

/**
 * Create a dialog service based on a component
 * @example
 * ```typescript
 * // prepare the container
 * container.bind(DialogManager).to(DialogManager);
 * container.bind(RouteGuard).toConstantValue(router);
 * 
 * container.bind('SampleDialogService').toDynamicValue(
 *      createDialog(() => import('@/path/to/SampleDialog.vue'))
 * )
 * 
 * // inject the dialog service
 * class SampleService {
 *     constructor(
 *          @inject('SampleDialogService')
 *          private readonly _sampleDialogService: AbstractDialogInputService<{ title }, string>;
 *     ){}
 * 
 *     private _onReceived(data: string, next: () => void) {}
 * 
 *     private _onAborted() {}
 *     
 *     doSomething() {
 *          this._sampleDialogService.onReceived(this._onReceived.bind(this));
 *          this._sampleDialogService.onAborted(this._onAborted.bind(this));
 *          this._sampleDialogService.request({ title: 'Hello' });
 *     }
 * }
 * ```
 */
export function createDialog(component: AsyncComponent) {
    return () => createDialogInstance(component);
}

const dialogMetadataKey = Symbol('dialog');

/**
 * Decorator to inject a dialog service into a property
 * @param value 
 * @returns 
 */
export function dialog(value: AsyncComponent) {
    function attributeDecorator(target: any, propertyKey: string) {
        let instance = Reflect.getOwnMetadata(dialogMetadataKey, target, propertyKey);
        if (!instance) {
            instance = createDialogInstance(value);
            Reflect.defineMetadata(dialogMetadataKey, instance, target, propertyKey);
        }
        defineProperty(target, propertyKey, instance);
    };

    return attributeDecorator;
}